import React, { useEffect, useMemo, useRef, useState } from "react";

import { makeStyles, useTheme } from "@material-ui/core/styles";
import {
	Drawer,
	IconButton,
	Typography,
	TextField,
	InputAdornment,
	Chip,
	CircularProgress,
	Button,
	Table,
	TableHead,
	TableBody,
	TableRow,
	TableCell,
	useMediaQuery,
	Divider,
	Box,
} from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import SearchIcon from "@material-ui/icons/Search";
import GroupIcon from "@material-ui/icons/Group";
import FiberManualRecordIcon from "@material-ui/icons/FiberManualRecord";

import api from "../../services/api";
import { i18n } from "../../translate/i18n";
import moment from "moment";

const useStyles = makeStyles((theme) => ({
	drawerPaper: {
		width: "94vw",
		maxWidth: 2000,
		[theme.breakpoints.down("sm")]: {
			width: "100%",
		},
	},
	header: {
		display: "flex",
		alignItems: "center",
		justifyContent: "space-between",
		padding: theme.spacing(2),
		borderBottom: `1px solid ${theme.palette.divider}`,
		gap: theme.spacing(1),
	},
	headerTitle: {
		display: "flex",
		alignItems: "center",
		gap: theme.spacing(1),
		minWidth: 0,
	},
	companyName: {
		fontWeight: 600,
		whiteSpace: "nowrap",
		overflow: "hidden",
		textOverflow: "ellipsis",
	},
	subtitle: {
		fontSize: "0.85rem",
		color: theme.palette.text.secondary,
	},
	searchWrap: {
		padding: theme.spacing(1, 2),
		borderBottom: `1px solid ${theme.palette.divider}`,
	},
	body: {
		flex: 1,
		minHeight: 0,
		overflowY: "auto",
		overflowX: "auto",
	},
	sectionHeader: {
		padding: theme.spacing(1.5, 2, 0.5),
		fontWeight: 600,
		fontSize: "0.85rem",
		color: theme.palette.text.secondary,
		textTransform: "uppercase",
		letterSpacing: 0.8,
	},
	stateWrap: {
		display: "flex",
		flexDirection: "column",
		alignItems: "center",
		justifyContent: "center",
		gap: theme.spacing(1),
		padding: theme.spacing(6, 2),
		textAlign: "center",
	},
	errorText: {
		color: theme.palette.error.main,
	},
	queueChip: {
		marginRight: 4,
		marginBottom: 4,
		height: 22,
		fontSize: "0.72rem",
	},
	noQueue: {
		color: theme.palette.text.secondary,
		fontStyle: "italic",
		fontSize: "0.85rem",
	},
	profileBadge: {
		display: "inline-block",
		padding: "2px 6px",
		borderRadius: 4,
		fontSize: "0.7rem",
		fontWeight: 600,
		textTransform: "uppercase",
		letterSpacing: 0.4,
	},
	profileAdmin: {
		background: "#e3f2fd",
		color: "#1565c0",
	},
	profileUser: {
		background: "#f1f8e9",
		color: "#558b2f",
	},
	onlineIcon: {
		fontSize: 10,
		marginRight: 4,
	},
	root: {
		display: "flex",
		flexDirection: "column",
		height: "100%",
	},
}));

const CompanyUsersDrawer = ({ open, onClose, company }) => {
	const classes = useStyles();
	const theme = useTheme();
	const isSmall = useMediaQuery(theme.breakpoints.down("sm"));

	const [users, setUsers] = useState([]);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState(null);
	const [search, setSearch] = useState("");

	const requestIdRef = useRef(0);

	const companyId = company?.id;
	const companyName = company?.name || "";
	const planCap = company?.plan?.users;

	const fetchUsers = async () => {
		if (!companyId) return;
		const myRequestId = ++requestIdRef.current;
		setLoading(true);
		setError(null);
		try {
			const { data } = await api.get(`/companies/${companyId}/users`);
			if (requestIdRef.current !== myRequestId) return;
			setUsers(Array.isArray(data) ? data : []);
		} catch (err) {
			if (requestIdRef.current !== myRequestId) return;
			setError(
				err?.response?.data?.error ||
					"Não foi possível carregar os funcionários"
			);
			setUsers([]);
		} finally {
			if (requestIdRef.current === myRequestId) {
				setLoading(false);
			}
		}
	};

	useEffect(() => {
		if (open && companyId) {
			setSearch("");
			fetchUsers();
		} else if (!open) {
			setUsers([]);
			setError(null);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [open, companyId]);

	const filteredUsers = useMemo(() => {
		const q = search.trim().toLowerCase();
		if (!q) return users;
		return users.filter(
			(u) =>
				(u.name || "").toLowerCase().includes(q) ||
				(u.email || "").toLowerCase().includes(q)
		);
	}, [users, search]);

	const admins = useMemo(() => filteredUsers.filter((u) => u.profile === "admin"), [filteredUsers]);
	const regular = useMemo(() => filteredUsers.filter((u) => u.profile !== "admin"), [filteredUsers]);

	const totalLabel = useMemo(() => {
		const total = users.length;
		if (Number.isFinite(planCap) && planCap > 0) {
			return `${total} / ${planCap}`;
		}
		return `${total}`;
	}, [users.length, planCap]);

	const renderQueues = (queues) => {
		if (!queues || queues.length === 0) {
			return <span className={classes.noQueue}>Sem setor</span>;
		}
		return queues.map((q) => (
			<Chip
				key={q.id}
				size="small"
				label={q.name}
				className={classes.queueChip}
				style={{ backgroundColor: q.color || "#9e9e9e", color: "#fff" }}
			/>
		));
	};

	const renderOnline = (online) => (
		<Box display="flex" alignItems="center">
			<FiberManualRecordIcon
				className={classes.onlineIcon}
				style={{ color: online ? "#4caf50" : "#bdbdbd" }}
			/>
			<span style={{ fontSize: "0.8rem" }}>{online ? "Online" : "Offline"}</span>
		</Box>
	);

	const renderLastSeen = (lastSeen) => {
		if (!lastSeen) return "—";
		return moment(lastSeen).format("DD/MM/YYYY HH:mm");
	};

	const renderProfile = (profile) => {
		const isAdmin = profile === "admin";
		return (
			<span className={`${classes.profileBadge} ${isAdmin ? classes.profileAdmin : classes.profileUser}`}>
				{isAdmin ? "Admin" : "Funcionário"}
			</span>
		);
	};

	const renderWorkHours = (start, end) => {
		if (!start && !end) return <span style={{ color: "#9e9e9e", fontSize: "0.8rem" }}>—</span>;
		return <span style={{ fontSize: "0.8rem", whiteSpace: "nowrap" }}>{start || "?"} – {end || "?"}</span>;
	};

	const renderFlag = (val) => {
		if (val === null || val === undefined || val === "") return <span style={{ color: "#9e9e9e", fontSize: "0.8rem" }}>—</span>;
		const yes = val === "enabled" || val === "true" || val === true || val === "1";
		return (
			<span style={{
				display: "inline-block", padding: "1px 6px", borderRadius: 3,
				fontSize: "0.7rem", fontWeight: 600,
				background: yes ? "#e8f5e9" : "#ffebee",
				color: yes ? "#2e7d32" : "#c62828",
			}}>
				{yes ? "Sim" : "Não"}
			</span>
		);
	};

	const userTable = (list, emptyMsg) => {
		if (list.length === 0) {
			return (
				<Box px={2} pb={1}>
					<Typography variant="body2" color="textSecondary">{emptyMsg}</Typography>
				</Box>
			);
		}
		return (
			<Table size="small">
				<TableHead>
					<TableRow>
						<TableCell>Nome</TableCell>
						<TableCell>Email</TableCell>
						<TableCell>Perfil</TableCell>
						<TableCell>Status</TableCell>
						<TableCell>Última atividade</TableCell>
						<TableCell>Cadastrado em</TableCell>
						<TableCell>Horário</TableCell>
						<TableCell>Ver todos tickets</TableCell>
						<TableCell>Conexões</TableCell>
						<TableCell>Setores</TableCell>
					</TableRow>
				</TableHead>
				<TableBody>
					{list.map((u) => (
						<TableRow key={u.id} hover>
							<TableCell style={{ whiteSpace: "nowrap" }}>{u.name}</TableCell>
							<TableCell>{u.email}</TableCell>
							<TableCell>{renderProfile(u.profile)}</TableCell>
							<TableCell>{renderOnline(u.online)}</TableCell>
							<TableCell style={{ whiteSpace: "nowrap" }}>{renderLastSeen(u.lastSeen)}</TableCell>
							<TableCell style={{ whiteSpace: "nowrap" }}>
								{u.createdAt ? moment(u.createdAt).format("DD/MM/YYYY") : "—"}
							</TableCell>
							<TableCell>{renderWorkHours(u.startWork, u.endWork)}</TableCell>
							<TableCell>{renderFlag(u.allTicket)}</TableCell>
							<TableCell>{renderFlag(u.allowConnections)}</TableCell>
							<TableCell>{renderQueues(u.queues)}</TableCell>
						</TableRow>
					))}
				</TableBody>
			</Table>
		);
	};

	return (
		<Drawer
			anchor={isSmall ? "bottom" : "right"}
			open={open}
			onClose={onClose}
			classes={{ paper: classes.drawerPaper }}
		>
			<div className={classes.root}>
				<div className={classes.header}>
					<div className={classes.headerTitle}>
						<GroupIcon color="primary" />
						<div style={{ minWidth: 0 }}>
							<Typography variant="subtitle1" className={classes.companyName} title={companyName}>
								{companyName}
							</Typography>
							<Typography className={classes.subtitle}>
								Total: {totalLabel} usuário(s) — {admins.length} admin(s), {regular.length} funcionário(s)
							</Typography>
						</div>
					</div>
					<IconButton onClick={onClose} aria-label="close">
						<CloseIcon />
					</IconButton>
				</div>

				<div className={classes.searchWrap}>
					<TextField
						fullWidth
						size="small"
						variant="outlined"
						placeholder="Buscar por nome ou email"
						value={search}
						onChange={(e) => setSearch(e.target.value)}
						InputProps={{
							startAdornment: (
								<InputAdornment position="start">
									<SearchIcon fontSize="small" />
								</InputAdornment>
							),
						}}
					/>
				</div>

				<div className={classes.body}>
					{loading && (
						<div className={classes.stateWrap}>
							<CircularProgress size={32} />
						</div>
					)}

					{!loading && error && (
						<div className={classes.stateWrap}>
							<Typography className={classes.errorText}>{error}</Typography>
							<Button variant="outlined" color="primary" size="small" onClick={fetchUsers}>
								Tentar novamente
							</Button>
						</div>
					)}

					{!loading && !error && users.length === 0 && (
						<div className={classes.stateWrap}>
							<Typography color="textSecondary">
								Esta empresa ainda não tem funcionários
							</Typography>
						</div>
					)}

					{!loading && !error && users.length > 0 && (
						<>
							<Typography className={classes.sectionHeader}>
								Administradores ({admins.length})
							</Typography>
							{userTable(admins, "Nenhum administrador encontrado.")}

							<Divider style={{ margin: "8px 0" }} />

							<Typography className={classes.sectionHeader}>
								Usuários ({regular.length})
							</Typography>
							{userTable(regular, "Nenhum usuário encontrado.")}
						</>
					)}
				</div>
			</div>
		</Drawer>
	);
};

export default CompanyUsersDrawer;
